/**
 * DataBest — ISP Uploader + Parser.
 * Handles: file selection (drag & drop or click), upload, background parse
 * with polling, and shows a preview of the parsed tree with an "Apply" action.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
    Upload,
    FileText,
    CheckCircle2,
    AlertTriangle,
    Loader2,
    Sparkles,
    ArrowRight,
    Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { plansApi } from "@/lib/planApi";
import PlanTree from "./PlanTree";

const POLL_INTERVAL_MS = 4000;
const POLL_MAX_ATTEMPTS = 90; // ~6 minutes

const ISPUploader = ({ plan, onApplied }) => {
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [parsing, setParsing] = useState(false);
    const [status, setStatus] = useState(null); // 'processing' | 'ready' | 'error' | 'not_started'
    const [parsed, setParsed] = useState(null);
    const [error, setError] = useState(null);
    const [applying, setApplying] = useState(false);

    // On mount / plan change: check the current parse status.
    useEffect(() => {
        let mounted = true;
        const check = async () => {
            try {
                const env = await plansApi.ispParsedGet(plan.id);
                if (!mounted) return;
                const st = env?._meta?.status || "not_started";
                setStatus(st);
                if (st === "ready") {
                    setParsed(env.parsed);
                } else if (st === "error") {
                    setError(env._meta.error);
                } else if (st === "processing") {
                    startPolling();
                }
            } catch (_) {
                // ignore
            }
        };
        check();
        return () => {
            mounted = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [plan.id]);

    const startPolling = useCallback(async () => {
        setParsing(true);
        setError(null);
        for (let i = 0; i < POLL_MAX_ATTEMPTS; i++) {
            await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
            try {
                const env = await plansApi.ispParsedGet(plan.id);
                const st = env?._meta?.status;
                setStatus(st);
                if (st === "ready") {
                    setParsed(env.parsed);
                    setParsing(false);
                    toast.success("ISP parsed successfully");
                    return;
                }
                if (st === "error") {
                    setError(env._meta.error);
                    setParsing(false);
                    toast.error("ISP parsing failed");
                    return;
                }
            } catch (err) {
                // network hiccup — keep polling
            }
        }
        setParsing(false);
        setError("Timed out waiting for parse to complete");
    }, [plan.id]);

    const handleFile = (f) => {
        if (!f) return;
        if (
            f.type !== "application/pdf" &&
            !f.name.toLowerCase().endsWith(".pdf")
        ) {
            toast.error("Please select a PDF file");
            return;
        }
        setFile(f);
    };

    const handleUpload = async () => {
        if (!file) return;
        setUploading(true);
        setError(null);
        try {
            await plansApi.ispUpload(plan.id, file);
            toast.success("ISP uploaded. Starting AI parsing…");
            await plansApi.ispParseStart(plan.id);
            setStatus("processing");
            startPolling();
        } catch (err) {
            toast.error(err?.message || "Upload failed");
            setError(err?.message || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const handleApply = async (forceDeleteMeasurements = false) => {
        if (!parsed) return;
        setApplying(true);
        try {
            const result = await plansApi.ispApply(
                plan.id,
                parsed,
                true,
                forceDeleteMeasurements,
            );
            toast.success(
                `Applied: ${result.outcomes_created} outcomes · ${result.activities_created} activities · ${result.tasks_created} tasks`,
            );
            onApplied?.();
        } catch (err) {
            // Backend refuses with 409 when replacing would cascade-delete
            // existing measurements. Prompt the user before losing data.
            const detail = err?.data?.detail;
            if (
                err?.status === 409 &&
                detail &&
                typeof detail === "object" &&
                detail.field === "replace_existing"
            ) {
                const message =
                    (detail.message || "This will delete existing measurements.") +
                    "\n\n" +
                    (detail.hint ||
                        "Historical scoring cannot be recovered. Continue?");
                if (window.confirm(message)) {
                    setApplying(false);
                    return handleApply(true);
                }
                toast("Apply cancelled — no data was changed.");
            } else {
                toast.error(err?.message || "Apply failed");
            }
        } finally {
            setApplying(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        const f = e.dataTransfer.files?.[0];
        handleFile(f);
    };

    const renderStatus = () => {
        if (status === "processing" || parsing) {
            return (
                <div className="flex items-center gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>
                        Claude is parsing the ISP. This can take 1–3 minutes
                        for a full plan. We&apos;ll update this view automatically
                        when done.
                    </span>
                </div>
            );
        }
        if (status === "error") {
            return (
                <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900">
                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
                    <div>
                        <p className="font-semibold">Parsing failed</p>
                        <p className="mt-1 text-xs">{error}</p>
                    </div>
                </div>
            );
        }
        if (status === "ready" && parsed) {
            const total = (parsed.outcomes || []).reduce(
                (acc, o) => ({
                    outcomes: acc.outcomes + 1,
                    activities:
                        acc.activities + (o.activities?.length || 0),
                    tasks:
                        acc.tasks +
                        (o.activities?.reduce(
                            (a, act) => a + (act.tasks?.length || 0),
                            0,
                        ) || 0),
                }),
                { outcomes: 0, activities: 0, tasks: 0 },
            );
            const applyBlocked = plan.status === "active";
            return (
                <div className="rounded-xl border border-brand-green-200 bg-brand-green-50 p-4">
                    <div className="flex items-start gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 text-brand-green-700" />
                        <div className="flex-1">
                            <p className="font-heading font-bold text-brand-green-900">
                                Parsed successfully
                            </p>
                            <p className="mt-1 text-sm text-brand-green-800">
                                Detected{" "}
                                <b>{total.outcomes} outcomes</b>,{" "}
                                <b>{total.activities} activities</b> and{" "}
                                <b>{total.tasks} tasks</b>. Review below, then
                                click <b>Apply to plan</b> to persist.
                            </p>
                            {applyBlocked && (
                                <p
                                    className="mt-2 text-xs font-semibold text-amber-800"
                                    data-testid="isp-apply-blocked-hint"
                                >
                                    Plan is already active — deactivate it
                                    first to re-apply the ISP.
                                </p>
                            )}
                        </div>
                        <button
                            onClick={() => handleApply(false)}
                            disabled={applying || applyBlocked}
                            data-testid="isp-apply-button"
                            title={
                                applyBlocked
                                    ? "Deactivate the plan first"
                                    : undefined
                            }
                            className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-4 text-sm font-semibold text-white hover:bg-brand-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                        >
                            {applying ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                                <ArrowRight className="h-4 w-4" />
                            )}
                            Apply to plan
                        </button>
                    </div>
                </div>
            );
        }
        return null;
    };

    return (
        <div className="space-y-4" data-testid="isp-uploader">
            {/* Active plans lock the ISP ingestion section — uploading, parsing
                or re-applying would either be blocked (Rule 1) or destroy the
                existing tree. We keep the "ISP PDF uploaded" info card below
                when a PDF exists so the supervisor sees what's attached. */}
            {plan.status === "active" && (
                <div
                    className="flex items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"
                    data-testid="isp-locked-active"
                >
                    <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-brand-green-700" />
                    <div>
                        <p className="font-semibold text-brand-blue-950">
                            ISP ingestion locked
                        </p>
                        <p className="mt-1 text-xs">
                            This plan is <b>active</b> and already has a
                            materialised outcome tree (see “Current plan
                            structure” below). Deactivate the plan first if
                            you need to upload or re-parse an ISP PDF.
                        </p>
                    </div>
                </div>
            )}

            {plan.status !== "active" && !plan.isp_pdf_url && !file && (
                <div
                    onDrop={onDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="rounded-2xl border-2 border-dashed border-brand-blue-200 bg-brand-blue-50/30 p-8 text-center"
                >
                    <Upload className="mx-auto h-10 w-10 text-brand-blue-400" />
                    <p className="mt-3 font-semibold text-brand-blue-950">
                        Drop the ISP PDF here
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                        or click to select — max 20MB
                    </p>
                    <input
                        type="file"
                        accept="application/pdf,.pdf"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        className="mt-4 block w-full max-w-xs cursor-pointer rounded-lg border border-brand-blue-200 bg-white p-2 text-sm text-slate-700"
                        data-testid="isp-file-input"
                    />
                </div>
            )}

            {file && !plan.isp_pdf_url && (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <FileText className="h-5 w-5 text-brand-blue-700" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">
                            {file.name}
                        </p>
                        <p className="text-xs text-slate-500">
                            {(file.size / 1024).toFixed(0)} KB
                        </p>
                    </div>
                    <button
                        onClick={() => setFile(null)}
                        className="rounded-md p-2 text-slate-500 hover:bg-slate-100"
                        aria-label="Remove"
                    >
                        <Trash2 className="h-4 w-4" />
                    </button>
                    <button
                        onClick={handleUpload}
                        disabled={uploading}
                        data-testid="isp-upload-button"
                        className="inline-flex h-10 items-center gap-2 rounded-lg bg-brand-blue-700 px-4 text-sm font-semibold text-white hover:bg-brand-blue-800 disabled:opacity-70"
                    >
                        {uploading ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                            <Sparkles className="h-4 w-4" />
                        )}
                        Upload &amp; parse
                    </button>
                </div>
            )}

            {plan.isp_pdf_url && (
                <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3">
                    <FileText className="h-5 w-5 text-brand-blue-700" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-800">
                            ISP PDF uploaded
                        </p>
                        <p className="text-xs text-slate-500">
                            {plan.isp_pdf_url.split("/").pop()}
                        </p>
                    </div>
                    {plan.status !== "active" &&
                      status !== "processing" &&
                      status !== "ready" && (
                        <button
                            onClick={async () => {
                                try {
                                    await plansApi.ispParseStart(plan.id);
                                    setStatus("processing");
                                    startPolling();
                                } catch (err) {
                                    toast.error(err?.message);
                                }
                            }}
                            data-testid="isp-reparse-button"
                            className="inline-flex h-10 items-center gap-2 rounded-lg border border-brand-blue-200 bg-white px-4 text-sm font-semibold text-brand-blue-700 hover:bg-brand-blue-50"
                        >
                            <Sparkles className="h-4 w-4" /> Re-parse with AI
                        </button>
                    )}
                </div>
            )}

            {/* Post-parse UI (Parsed successfully card + Apply button + AI preview)
                is only relevant when the plan is NOT active. On active plans the
                tree has already been materialised (see "Current plan structure"
                below) so re-applying would be blocked (Rule 1) or destructive. */}
            {plan.status !== "active" && renderStatus()}

            {plan.status !== "active" && parsed && (
                <div>
                    <h3 className="mb-3 font-heading text-lg font-bold text-brand-blue-950">
                        AI preview — Plan structure
                    </h3>
                    <PlanTree outcomes={parsed.outcomes} />
                </div>
            )}
        </div>
    );
};

export default ISPUploader;
